/**
 * Automation Preset Loader
 * 从 JSON 预设加载 automation envelopes
 */

import { AutomationEnvelope, AutomationPoint, CurveType } from './AutomationEnvelope'
import automationPresetsData from '@/data/automationPresets.json'

// JSON 预设中的点格式
interface PresetPoint {
  position: number  // 0-1 之间的相对位置
  value: number     // 0-1 之间的值
  curveType: string // 曲线类型字符串
}

// 预设中的曲线定义
interface PresetCurve {
  name: string
  description: string
  [key: string]: any // 可以包含各种轨道的 envelope 定义
}

// 完整的预设数据结构
interface AutomationPresetsData {
  volumeCurves: Record<string, PresetCurve>
  eqCurves: Record<string, PresetCurve>
  filterTypes: Record<string, PresetCurve>
}

/**
 * 将字符串曲线类型转换为 CurveType 枚举
 */
function parseCurveType(curveTypeStr: string): CurveType {
  switch (curveTypeStr.toLowerCase()) {
    case 'linear':
      return CurveType.LINEAR
    case 'exponential':
      return CurveType.EXPONENTIAL
    case 'logarithmic':
      return CurveType.LOGARITHMIC
    case 'smooth':
      return CurveType.SMOOTH
    case 'step':
      return CurveType.STEP
    default:
      return CurveType.SMOOTH
  }
}

/**
 * 从预设点数组创建 AutomationEnvelope
 * @param presetPoints 预设点数组（position 和 value 都是 0-1）
 * @param startTime 转场开始时间（秒）
 * @param endTime 转场结束时间（秒）
 */
function createEnvelopeFromPreset(
  presetPoints: PresetPoint[],
  startTime: number,
  endTime: number
): AutomationEnvelope {
  const envelope = new AutomationEnvelope()
  const duration = endTime - startTime
  
  for (const point of presetPoints) {
    const automationPoint: AutomationPoint = {
      time: startTime + point.position * duration,
      value: point.value,
      curveType: parseCurveType(point.curveType)
    }
    envelope.addPoint(automationPoint)
  }
  
  return envelope
}

/**
 * Automation Preset Loader 类
 */
export class AutomationPresetLoader {
  private static presetsData: AutomationPresetsData = automationPresetsData as AutomationPresetsData
  
  /**
   * 加载 Volume Curve 预设
   */
  static loadVolumeCurve(
    presetName: string,
    startTime: number,
    endTime: number
  ): {
    track1Volume: AutomationEnvelope
    track2Volume: AutomationEnvelope
  } | null {
    const preset = this.presetsData.volumeCurves[presetName]
    if (!preset) {
      console.error(`Volume curve preset not found: ${presetName}`)
      return null
    }
    
    return {
      track1Volume: createEnvelopeFromPreset(preset.track1Volume, startTime, endTime),
      track2Volume: createEnvelopeFromPreset(preset.track2Volume, startTime, endTime)
    }
  }
  
  /**
   * 加载 EQ Curve 预设
   */
  static loadEQCurve(
    presetName: string,
    startTime: number,
    endTime: number
  ): {
    track1EQLow: AutomationEnvelope
    track1EQMid: AutomationEnvelope
    track1EQHigh: AutomationEnvelope
    track2EQLow: AutomationEnvelope
    track2EQMid: AutomationEnvelope
    track2EQHigh: AutomationEnvelope
  } | null {
    const preset = this.presetsData.eqCurves[presetName]
    if (!preset) {
      console.error(`EQ curve preset not found: ${presetName}`)
      return null
    }
    
    return {
      track1EQLow: createEnvelopeFromPreset(preset.track1EQLow, startTime, endTime),
      track1EQMid: createEnvelopeFromPreset(preset.track1EQMid, startTime, endTime),
      track1EQHigh: createEnvelopeFromPreset(preset.track1EQHigh, startTime, endTime),
      track2EQLow: createEnvelopeFromPreset(preset.track2EQLow, startTime, endTime),
      track2EQMid: createEnvelopeFromPreset(preset.track2EQMid, startTime, endTime),
      track2EQHigh: createEnvelopeFromPreset(preset.track2EQHigh, startTime, endTime)
    }
  }
  
  /**
   * 加载 Filter Type 预设
   */
  static loadFilterType(
    presetName: string,
    startTime: number,
    endTime: number
  ): {
    track1Filter: AutomationEnvelope
    track2Filter: AutomationEnvelope
  } | null {
    const preset = this.presetsData.filterTypes[presetName]
    if (!preset) {
      console.error(`Filter type preset not found: ${presetName}`)
      return null
    }
    
    return {
      track1Filter: createEnvelopeFromPreset(preset.track1Filter, startTime, endTime),
      track2Filter: createEnvelopeFromPreset(preset.track2Filter, startTime, endTime)
    }
  }
  
  /**
   * 获取所有可用的 Volume Curve 预设名称
   */
  static getVolumeCurveNames(): string[] {
    return Object.keys(this.presetsData.volumeCurves)
  }
  
  /**
   * 获取所有可用的 EQ Curve 预设名称
   */
  static getEQCurveNames(): string[] {
    return Object.keys(this.presetsData.eqCurves)
  }
  
  /**
   * 获取所有可用的 Filter Type 预设名称
   */
  static getFilterTypeNames(): string[] {
    return Object.keys(this.presetsData.filterTypes)
  }
  
  /**
   * 获取预设的元数据（名称和描述）
   */
  static getVolumeCurveInfo(presetName: string): { name: string; description: string } | null {
    const preset = this.presetsData.volumeCurves[presetName]
    if (!preset) return null
    return { name: preset.name, description: preset.description }
  }
  
  static getEQCurveInfo(presetName: string): { name: string; description: string } | null {
    const preset = this.presetsData.eqCurves[presetName]
    if (!preset) return null
    return { name: preset.name, description: preset.description }
  }
  
  static getFilterTypeInfo(presetName: string): { name: string; description: string } | null {
    const preset = this.presetsData.filterTypes[presetName]
    if (!preset) return null
    return { name: preset.name, description: preset.description }
  }
}

